/**
 * Unified Context Provider
 *
 * Combines all domain-specific contexts into a single provider tree
 * Replaces the monolithic AppContext with modular, domain-separated contexts
 */

import type { ReactNode } from 'react'
import { ComponentDataProvider } from './ComponentDataContext'
import { FilterStateProvider } from './FilterStateContext'
import { CompatibilityProvider } from './CompatibilityContext'
import { AuthProvider } from './AuthContext'
import { UserDataSyncProvider } from './UserDataSyncContext'

/**
 * Combined provider that wraps all domain contexts
 */
export function AppContextProvider({ children }: { children: ReactNode }) {
  return (
    <AuthProvider>
      <UserDataSyncProvider>
        <ComponentDataProvider>
          <FilterStateProvider>
            <CompatibilityProvider>
              {children}
            </CompatibilityProvider>
          </FilterStateProvider>
        </ComponentDataProvider>
      </UserDataSyncProvider>
    </AuthProvider>
  )
}

// Re-export all hooks for easy access
export { useAuth } from './AuthContext'
export { useComponentData } from './ComponentDataContext'
export { useFilterState } from './FilterStateContext'
export { useCompatibility } from './CompatibilityContext'

// Re-export types for external use
export type { ComponentDataState } from './ComponentDataContext'
export type { FilterStateState } from './FilterStateContext'
export type { CompatibilityState, ComponentConfiguration, CompatibilityIssue, CompatibilityCheckResult } from './CompatibilityContext'